import { htmlEscape } from "../utils/vanilla";
import { getDateTokenString, dstCalculator, compareDates } from "./date";
import { LocalizerService } from "./localizer";
import { DriveStore } from "../store/reducers";
import { makeDeviceCache } from "./storage/cache";
import type { ISystemSettings } from "../store/systemSettings/types";
import { isInCanada, type THosStatus } from "hos-tools";
import DutyStatusLog, { NOUSERID } from "../model/DutyStatusLog";
import { DutyStatusLogService, IDailyOdometer } from "../model/DutyStatusLogs";
import HosProcess from "../services/hosProcess";

/**
 * Defines the interface used for displaying Odometer Start/End readings on logs/inspection pages.
 * */
export interface IOdometerRecord {
    deviceName: string;
    start: string;
    end: string;
    id: string;
}

export let getCustomExemptHOSName = (systemSettings: DeepReadonlyObject<ISystemSettings>): THosStatus => {
    let customName = "Exempted HOS";
    if (systemSettings?.customExemptHOSName) {
        customName = systemSettings.customExemptHOSName.toString();
    }
    return htmlEscape(customName) as THosStatus;
};

export let getCustomExemptHOSAbbreviation = (systemSettings: DeepReadonlyObject<ISystemSettings>): THosStatus => {
    let customAbbreviation = "XEMP";
    if (systemSettings?.customExemptHOSAbbreviation) {
        customAbbreviation = systemSettings.customExemptHOSAbbreviation.toString();
    }
    return htmlEscape(customAbbreviation) as THosStatus;
};

export let getTranslatedStatus = (status: THosStatus | undefined, customExemptHOSAbbreviation: string, getString: (key: string) => string): string => {
    if (!status) {
        return "";
    }
    if (status === "PC_Exempted") {
        return customExemptHOSAbbreviation;
    }
    const statusMap = {
        "OFF": getString("OFF"),
        "SB": getString("SB"),
        "D": getString("D"),
        "ON": getString("ON"),
        "PC": getString("PC"),
        "YM": getString("YM"),
        "WT": getString("WT")
    };
    return statusMap[status] || status;
};

export let getOdometer = (date: Date, localizer: LocalizerService, store: DriveStore, userId: string | undefined, historicalOdometer?: IDailyOdometer): IOdometerRecord[] => {
    let odometers: IOdometerRecord[] = [];
    // Historical days pass their cached odometer fragment via historicalOdometer; in-window days read
    // the shared dailyOdometer store keyed by user.
    let dailyOdometer = historicalOdometer ?? (userId ? store.getState().dailyOdometer[userId] || {} : {});
    let dateString = getDateTokenString(dstCalculator.toLocalDateTime(date));
    let vehicles = makeDeviceCache(store).get();

    //Get the daily odometers for each vehicle
    if (dailyOdometer.hasOwnProperty(dateString)) {
        for (let deviceId in dailyOdometer[dateString]) {
            if (dailyOdometer[dateString].hasOwnProperty(deviceId)) {
                let start = dailyOdometer[dateString][deviceId].start ? dailyOdometer[dateString][deviceId].start : 0;
                let end = dailyOdometer[dateString][deviceId].end ? dailyOdometer[dateString][deviceId].end : 0;
                if (!(start > 0 || end > 0)) {
                    continue;
                }

                let device = vehicles.filter(v => v.id === deviceId)[0];
                let deviceName = device && device.name ? device.name : "Unknown";
                if (end - start >= 0) {
                    odometers.push({ deviceName: `${ deviceName }`, start: localizer.toLocalDistanceString(start), end: localizer.toLocalDistanceString(end), id: deviceId });
                }
            }
        }
    }
    return odometers;
};

export let addLogDistance = (log: DutyStatusLog, dailyMetres: number, hosUtils: HosProcess, isInCanadaWithPC: boolean): [boolean, number] => {
    let isCurrentLogPCInCanada = isInCanadaWithPC;
    let newMetres = dailyMetres;
    if (log.isPCLog()) {
        let ruleSetBeforeLog = hosUtils.getUserHosRuleSets().getRulesetByDate(log.getDate());
        // PC distance do not count for canadian ELD
        if (ruleSetBeforeLog && isInCanada(ruleSetBeforeLog.hosRuleSet.id) && log.getDistance() > 0) {
            isCurrentLogPCInCanada = true;
        } else {
            newMetres += log.getDistance();
        }
    } else {
        newMetres += log.getDistance();
    }
    return [isCurrentLogPCInCanada, newMetres];
};

// Check if a datetime falls within a DWIE (Driving While In Exemption) period
export function isMostRecentStatusDWIE(targetDateTime: string, dutyStatusLogService: DutyStatusLogService): boolean {
    const systemLogs = dutyStatusLogService.getSystemHOSLogs();
    if (!systemLogs?.length) {
        return false;
    }

    const dwieLogsBeforeTarget = systemLogs.filter(log => log.dateTime <= targetDateTime && log.isDrivingWhileInExemptionRelatedLog());
    if (!dwieLogsBeforeTarget?.length) {
        return false;
    }

    const mostRecentLog = dwieLogsBeforeTarget.sort((a, b) => compareDates(a.dateTime, b.dateTime))[0];
    return mostRecentLog.status === "DrivingWhileInExemption";
}

export function shouldBlockEditingDLog(logToCheck: DutyStatusLog, dutyStatusLogService: DutyStatusLogService): boolean {
    if (logToCheck?.status !== "D" || logToCheck.isAutomaticLog()) {
        return false;
    }

    const deviceId = logToCheck.getDeviceId();
    if (!deviceId || deviceId === "NoDeviceId") {
        return false;
    }

    const driverId = logToCheck.getDriverId();
    if (!driverId || driverId === NOUSERID) {
        return false;
    }

    return isMostRecentStatusDWIE(logToCheck.dateTime, dutyStatusLogService);
}
